import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Link2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, API, errMsg, fmtDate } from "@/lib/api";

export const Card = ({ children, className = "", "data-testid": testId }) => <div className={`bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 ${className}`} data-testid={testId}>{children}</div>;

function Stat({ label, value, id }) {
  return <Card><p className="text-xs uppercase tracking-wide text-slate-500">{label}</p><p className="font-display text-3xl font-extrabold text-slate-900 mt-1 tabular-nums" data-testid={`stat-${id}`}>{value}</p></Card>;
}

export function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!s) return null;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Active students" value={s.students} id="students" />
        <Stat label="Revenue (paid)" value={`$${s.revenue.toFixed(2)}`} id="revenue" />
        <Stat label="Course completion" value={`${s.completion_rate}%`} id="completion" />
        <Stat label="Avg progress" value={`${s.avg_progress}%`} id="avg-progress" />
        <Stat label="Purchases" value={s.purchases} id="purchases" />
        <Stat label="Refunds" value={s.refunds} id="refunds" />
        <Stat label="Abandoned / pending" value={s.abandoned_or_pending} id="abandoned" />
        <Stat label="Coach helpful / not" value={`${s.coach_up} / ${s.coach_down}`} id="coach-feedback" />
      </div>
      <Card>
        <h2 className="font-bold text-slate-900 mb-4">Completion by module</h2>
        <div className="space-y-3">
          {s.modules.map((m) => (
            <div key={m.number} className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,320px)_1fr_auto] gap-3 items-center text-sm">
              <span className="truncate text-slate-700">{m.number}. {m.title}</span>
              <Progress value={m.rate} className="h-2 hidden sm:block" />
              <span className="tabular-nums text-slate-500 w-20 text-right">{m.completed} · {m.rate}%</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const PLANS = [["3day", "$197 plan — opens after 3 days"], ["1week", "$97 plan — opens after 1 week"], ["2week", "$57 plan — opens after 2 weeks"], ["standard", "Open immediately (post-promo $250 plan)"]];

function TestJourney({ onDone }) {
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("3day");
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/test-purchase", { email, tier });
      toast.success(data.email_sent ? `Test purchase created. Check ${email} for the "purchase confirmed" email.` : "Test purchase created, but the email failed to send.");
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card className="space-y-3 border-teal-200 bg-teal-50/40" data-testid="test-journey-card">
      <h2 className="font-bold text-slate-900">Test the student journey (no payment)</h2>
      <p className="text-sm text-slate-600">Use a personal email that is <strong>not</strong> your admin email, and open the email on the phone or browser you want to test with. You'll go through exactly what a buyer sees: confirmation email, create account, locked course with countdown, then "Open now" below sends the "you now have full access" email. Test accounts don't count as revenue. Running it again on the same email resets that test account.</p>
      <form onSubmit={run} className="flex flex-col md:flex-row gap-2">
        <Input type="email" required placeholder="your.personal@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white" data-testid="test-journey-email" />
        <select value={tier} onChange={(e) => setTier(e.target.value)} className="border border-slate-300 rounded-md px-2 py-2 text-sm bg-white" aria-label="Plan" data-testid="test-journey-tier">
          {PLANS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Button type="submit" disabled={busy} data-testid="test-journey-submit">{busy ? "Creating…" : "Send test purchase"}</Button>
      </form>
      <p className="text-xs text-slate-500">Prefer to test real checkout too? Stripe is in test mode: pay with card 4242 4242 4242 4242, any future date and any CVC. No real money is taken.</p>
    </Card>
  );
}

export function Students() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [email, setEmail] = useState("");
  const load = () => api.get("/admin/students").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const grant = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/students", { email, send_email: true }); toast.success("Access granted and welcome email sent"); setEmail(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const toggle = async (u) => { await api.post(`/admin/students/${u.id}/access`, { has_access: !u.has_access }); load(); };
  const link = async (u) => {
    const { data } = await api.post(`/admin/students/${u.id}/login-link`);
    await navigator.clipboard.writeText(data.link).catch(() => {});
    toast.success("One-time sign-in link copied (valid 24h)", { description: data.link });
  };
  const act = async (u, path, msg) => { try { await api.post(`/admin/students/${u.id}/${path}`); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const shown = rows.filter((r) => r.email.includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="admin-students">
      <TestJourney onDone={load} />
      <div className="flex flex-col md:flex-row gap-3 justify-between">
        <Input placeholder="Search by email" value={q} onChange={(e) => setQ(e.target.value)} className="md:max-w-xs bg-white" data-testid="students-search" />
        <div className="flex gap-2 flex-wrap">
          <form onSubmit={grant} className="flex gap-2"><Input type="email" required placeholder="Grant free access: email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white" data-testid="grant-access-email" /><Button type="submit" variant="outline" data-testid="grant-access-submit"><UserPlus className="h-4 w-4 mr-1" />Grant</Button></form>
          <Button asChild data-testid="export-students-button"><a href={`${API}/admin/students/export`}><Download className="h-4 w-4 mr-1.5" />Export CSV</a></Button>
        </div>
      </div>
      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Purchased</TableHead><TableHead>Plan / opens</TableHead><TableHead>Devices</TableHead><TableHead>Progress</TableHead><TableHead>Access</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {shown.map((u) => (
              <TableRow key={u.id} data-testid={`student-row-${u.email}`}>
                <TableCell className="font-medium">{u.email}{u.test_account && <span className="ml-2 text-[10px] font-bold uppercase text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">test</span>}{u.revoked_reason && <span className="ml-2 text-xs text-rose-600">({u.revoked_reason})</span>}</TableCell>
                <TableCell>{fmtDate(u.purchased_at) || "—"}</TableCell>
                <TableCell className="text-xs">{u.tier || "—"}{u.access_unlocks_at && <div className={new Date(u.access_unlocks_at) > new Date() ? "text-amber-700 font-semibold" : "text-slate-500"}>{new Date(u.access_unlocks_at) > new Date() ? "opens " : "opened "}{fmtDate(u.access_unlocks_at)}</div>}</TableCell>
                <TableCell className="text-xs">{u.devices}/2</TableCell>
                <TableCell>{u.completed}/11</TableCell>
                <TableCell><Switch checked={u.has_access} onCheckedChange={() => toggle(u)} aria-label="Course access" data-testid={`student-access-${u.email}`} /></TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => link(u)} data-testid={`student-login-link-${u.email}`}><Link2 className="h-4 w-4 mr-1" />Login link</Button>
                  <Button size="sm" variant="ghost" onClick={() => act(u, "reset-devices", "Devices reset. Their next sign-in becomes the trusted device.")} data-testid={`student-reset-devices-${u.email}`}>Reset devices</Button>
                  {u.access_unlocks_at && new Date(u.access_unlocks_at) > new Date() && <Button size="sm" variant="ghost" onClick={() => act(u, "unlock-now", "Course opened and 'full access' email sent")} data-testid={`student-unlock-now-${u.email}`}>Open now</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!shown.length && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No students yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export function Purchases() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/purchases").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const refund = async (t) => {
    if (!window.confirm(`Refund ${t.email} $${t.amount} via Stripe and revoke access?`)) return;
    try { await api.post(`/admin/purchases/${t.session_id}/refund`); toast.success("Refund issued and access revoked"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const badge = { paid: "bg-teal-50 text-teal-800", refunded: "bg-slate-100 text-slate-600", expired: "bg-amber-50 text-amber-800", failed: "bg-rose-50 text-rose-700", pending: "bg-sky-50 text-sky-800" };
  return (
    <Card className="p-0 overflow-x-auto" data-testid="admin-purchases">
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Email</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.session_id}>
              <TableCell>{fmtDate(t.created_at)}</TableCell>
              <TableCell>{t.email || "—"}{t.duplicate && <span className="ml-2 text-xs font-semibold text-amber-700">duplicate</span>}</TableCell>
              <TableCell>${Number(t.amount || 0).toFixed(2)} {String(t.currency || "").toUpperCase()}</TableCell>
              <TableCell><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge[t.payment_status] || ""}`}>{t.payment_status}</span></TableCell>
              <TableCell>{t.payment_status === "paid" && <Button size="sm" variant="outline" onClick={() => refund(t)} data-testid={`refund-${t.session_id}`}>Refund</Button>}</TableCell>
            </TableRow>
          ))}
          {!rows.length && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No checkout sessions yet</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}
